import _ from "lodash";
import request from "superagent";

import env from "../constants/env";
import state from "../state/state";
import { getJwtToken } from "../actions/user";
import { logWebLoginSequence, summarizeLocation } from "./webLoginSequenceLogger";

const GLOBAL_KEY = "__AMBIFI_WEB_DIAGNOSTICS__";
const CONSOLE_KEY = "__AMBIFI_WEB_DIAGNOSTIC_CONSOLE__";
const NETWORK_KEY = "__AMBIFI_WEB_DIAGNOSTIC_NETWORK__";
const CHILD_REPORTS_KEY = "__AMBIFI_WEB_DIAGNOSTIC_CHILD_REPORTS__";
const EVENTS_KEY = "__AMBIFI_WEB_DIAGNOSTIC_EVENTS__";
const TEMP_CAPTURE_UNTIL_KEY = "__AMBIFI_WEB_DIAGNOSTIC_CAPTURE_UNTIL__";
const TEMP_CAPTURE_REASON_KEY = "__AMBIFI_WEB_DIAGNOSTIC_CAPTURE_REASON__";
const ENABLED_STORAGE_KEY = "__ambifiWebDiagnosticsEnabled";
const MAX_CONSOLE_ENTRIES = 250;
const MAX_NETWORK_ENTRIES = 250;
const MAX_DIAGNOSTIC_EVENTS = 150;
const MAX_SEQUENCE_EVENTS = 500;
const MAX_RESOURCE_ENTRIES = 150;
const MAX_SEND_SEQUENCE_EVENTS = 200;
const MAX_SEND_CONSOLE_ENTRIES = 120;
const MAX_SEND_NETWORK_ENTRIES = 120;
const MAX_SEND_DIAGNOSTIC_EVENTS = 120;
const MAX_SEND_RESOURCE_ENTRIES = 80;
const MAX_SEND_STRING_LENGTH = 4000;
const STORAGE_KEYS = [
	"origin",
	"forceLoginV3",
	"hcaNativeMobileWebViewSeen",
	"hcaNativeCloudPortal",
	"hcaNativeSsoLoginRequestId",
	"hcaNativeSsoLoginRequestLaunchId",
	"hcaNativePostAuthDedupeRecord",
	"hcaNativeLogoutSuppressAutoSsoUntil",
	"hcaNativeLogoutCompleteNotified",
	"hcaNativeForceReauthUntil",
	"__ambifiPlayerLaunchLoopV1"
];
const USER_ISSUE_CATEGORIES = [
	["general", "General issue"],
	["audio", "Audio"],
	["stuck", "Page stuck"],
	["content", "Wrong content"],
	["input", "Inputs or checks"],
	["performance", "Performance"],
	["other", "Other"]
];
const USER_ISSUE_SEVERITIES = [
	["blocking", "Blocking me"],
	["annoying", "Annoying"],
	["feedback", "Feedback"]
];
const USER_ISSUE_CHILD_REPORT_WAIT_MS = 800;

function readBooleanParam(params, key) {
	const value = params.get(key);
	if (value === null) {
		return null;
	}
	const normalizedValue = _.toString(value || "").trim().toLowerCase();
	return normalizedValue === "1" || normalizedValue === "true" || normalizedValue === "yes";
}

function persistDiagnosticsPreferenceFromUrl() {
	if (typeof window === "undefined" || !window.location || !window.localStorage) {
		return;
	}

	try {
		const params = new URLSearchParams(window.location.search || "");
		const enabledParam = readBooleanParam(params, "ambifiDiagnostics");
		if (enabledParam === true) {
			window.localStorage.setItem(ENABLED_STORAGE_KEY, "true");
		} else if (enabledParam === false) {
			window.localStorage.removeItem(ENABLED_STORAGE_KEY);
		}
	} catch (_err) {
		// Diagnostics preference should never affect app startup.
	}
}

export function isWebDiagnosticsEnabled() {
	if (typeof window === "undefined") {
		return false;
	}

	try {
		const params = new URLSearchParams(window.location.search || "");
		const enabledParam = readBooleanParam(params, "ambifiDiagnostics");
		if (enabledParam !== null) {
			return enabledParam;
		}
		if (window.localStorage && window.localStorage.getItem(ENABLED_STORAGE_KEY) === "true") {
			return true;
		}
		const hostname = _.toString(_.get(window, ["location", "hostname"], "")).toLowerCase();
		return hostname === "localhost" || hostname.indexOf("local.") !== -1;
	} catch (_err) {
		return false;
	}
}

export function shouldAutoOpenWebDiagnostics() {
	if (typeof window === "undefined") {
		return false;
	}

	try {
		const params = new URLSearchParams(window.location.search || "");
		return readBooleanParam(params, "ambifiDiagnostics") === true;
	} catch (_err) {
		return false;
	}
}

function hasCurrentDiagnosticsParam() {
	if (typeof window === "undefined" || !window.location) {
		return false;
	}

	const search = _.toString(window.location.search || "");
	return /(?:[?&])ambifiDiagnostics=(?:1|true|yes)(?:&|$)/i.test(search);
}

function isPlayerLaunchDiagnosticsRoute() {
	if (typeof window === "undefined" || !window.location) {
		return false;
	}

	const pathname = _.toString(window.location.pathname || "");
	return pathname.indexOf("/player/") === 0 ||
		pathname.indexOf("/activity/") === 0 ||
		pathname === "/activity" ||
		pathname.indexOf("/login") === 0 ||
		pathname === "/auth" ||
		pathname === "/loggedIn";
}

function isDiagnosticsPanelOpen() {
	return typeof document !== "undefined" && !!document.getElementById("ambifi-web-diagnostics-panel");
}

function getTemporaryDiagnosticCaptureUntil() {
	if (typeof window === "undefined") {
		return 0;
	}

	const until = Number(window[TEMP_CAPTURE_UNTIL_KEY]);
	return Number.isFinite(until) ? until : 0;
}

function isTemporaryDiagnosticCaptureActive() {
	return getTemporaryDiagnosticCaptureUntil() > Date.now();
}

function getDiagnosticCaptureState() {
	if (typeof window === "undefined") {
		return {};
	}

	const captureUntil = getTemporaryDiagnosticCaptureUntil();
	return redactDeep({
		temporaryCaptureActive: captureUntil > Date.now(),
		temporaryCaptureUntil: captureUntil ? new Date(captureUntil).toISOString() : "",
		temporaryCaptureReason: _.toString(window[TEMP_CAPTURE_REASON_KEY] || "")
	});
}

function shouldRecordDiagnosticCapture() {
	return isDiagnosticsPanelOpen() || hasCurrentDiagnosticsParam() || isPlayerLaunchDiagnosticsRoute() || isTemporaryDiagnosticCaptureActive();
}

function redactText(value) {
	if (!_.isString(value) || value === "") {
		return value;
	}

	return value
		.replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [redacted]")
		.replace(/Authorization['":\s]+[A-Za-z0-9._~+/=-]+/gi, "Authorization: [redacted]")
		.replace(/([?&#](?:access_token|code|id_token|refresh_token|state|token|auth|authCallback|authCallbackV2|x-amz-signature|x-amz-credential|x-amz-security-token|awsaccesskeyid|signature)=)[^&#\s]+/gi, "$1[redacted]")
		.replace(/(^|[\s{,])((?:\\?["']?)(?:access[_-]?token|id[_-]?token|refresh[_-]?token|session[_-]?token|accessKeyId|secretAccessKey|randomPasswordKey|deviceKey|password|authorization|secret|apiKey|privateKey)(?:\\?["']?)\s*[:=]\s*(?:\\?["'])?)(?:\[[^\]]+\]|[^\\"',}\]\s]+)/gi, "$1$2[redacted]")
		.replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, "[redacted-jwt]");
}

function shouldRedactKey(key) {
	const normalizedKey = _.toString(key).toLowerCase().replace(/[^a-z0-9]/g, "");
	return normalizedKey.indexOf("token") !== -1 ||
		normalizedKey.indexOf("authorization") !== -1 ||
		normalizedKey.indexOf("password") !== -1 ||
		normalizedKey.indexOf("secret") !== -1 ||
		normalizedKey.indexOf("accesskey") !== -1 ||
		normalizedKey.indexOf("sessiontoken") !== -1 ||
		normalizedKey.indexOf("apikey") !== -1 ||
		normalizedKey.indexOf("privatekey") !== -1 ||
		normalizedKey.indexOf("devicekey") !== -1 ||
		normalizedKey === "auth" ||
		normalizedKey === "code";
}

function redactDeep(value, depth = 0) {
	if (depth > 6) {
		return "[max-depth]";
	}

	if (_.isString(value)) {
		return redactText(value);
	}

	if (_.isNil(value) || _.isNumber(value) || _.isBoolean(value)) {
		return value;
	}

	if (_.isArray(value)) {
		return value.slice(0, 100).map(item => redactDeep(item, depth + 1));
	}

	if (_.isPlainObject(value)) {
		return Object.keys(value).slice(0, 100).reduce((acc, key) => {
			if (shouldRedactKey(key)) {
				acc[key] = "[redacted]";
			} else {
				acc[key] = redactDeep(value[key], depth + 1);
			}
			return acc;
		}, {});
	}

	try {
		return redactText(JSON.stringify(value));
	} catch (_err) {
		return redactText(`${value}`);
	}
}

function compactForSend(value, depth = 0) {
	if (depth > 6) {
		return "[max-depth]";
	}

	if (_.isString(value)) {
		return value.length > MAX_SEND_STRING_LENGTH ? `${value.slice(0, MAX_SEND_STRING_LENGTH)}...[truncated]` : value;
	}

	if (_.isNil(value) || _.isNumber(value) || _.isBoolean(value)) {
		return value;
	}

	if (_.isArray(value)) {
		return value.slice(-100).map(item => compactForSend(item, depth + 1));
	}

	if (_.isPlainObject(value)) {
		return Object.keys(value).slice(0, 100).reduce((acc, key) => {
			acc[key] = compactForSend(value[key], depth + 1);
			return acc;
		}, {});
	}

	try {
		return compactForSend(JSON.stringify(value), depth + 1);
	} catch (_err) {
		return compactForSend(`${value}`, depth + 1);
	}
}

function buildSendReport(report = {}) {
	return compactForSend(_.assign({}, report, {
		webLoginSequenceEvents: _.isArray(report.webLoginSequenceEvents) ? report.webLoginSequenceEvents.slice(-MAX_SEND_SEQUENCE_EVENTS) : [],
		consoleEntries: _.isArray(report.consoleEntries) ? report.consoleEntries.slice(-MAX_SEND_CONSOLE_ENTRIES) : [],
		networkRequests: _.isArray(report.networkRequests) ? report.networkRequests.slice(-MAX_SEND_NETWORK_ENTRIES) : [],
		diagnosticEvents: _.isArray(report.diagnosticEvents) ? report.diagnosticEvents.slice(-MAX_SEND_DIAGNOSTIC_EVENTS) : [],
		performanceResources: _.isArray(report.performanceResources) ? report.performanceResources.slice(-MAX_SEND_RESOURCE_ENTRIES) : []
	}));
}

function safeJsonParse(value) {
	if (!_.isString(value) || value === "") {
		return value;
	}
	try {
		return JSON.parse(value);
	} catch (_err) {
		return value;
	}
}

function readStorage(storage) {
	if (!storage) {
		return {};
	}

	return STORAGE_KEYS.reduce((acc, key) => {
		try {
			const value = storage.getItem(key);
			if (value !== null) {
				acc[key] = redactDeep(safeJsonParse(value));
			}
		} catch (err) {
			acc[key] = `[unavailable: ${err && err.message ? err.message : err}]`;
		}
		return acc;
	}, {});
}

function getCurrentPath() {
	if (typeof window === "undefined" || !window.location) {
		return "";
	}
	return redactText(`${window.location.pathname || ""}${window.location.search || ""}${window.location.hash || ""}`);
}

function getLoggerEvents() {
	try {
		const logger = window.__AMBIFI_WEB_LOGIN_SEQUENCE_LOGGER__;
		const events = logger && _.isFunction(logger.getEvents) ? logger.getEvents() : [];
		return redactDeep((_.isArray(events) ? events : []).slice(-MAX_SEQUENCE_EVENTS));
	} catch (err) {
		return [{
			error: err && err.message ? err.message : `${err}`
		}];
	}
}

function getConsoleEntries() {
	const entries = typeof window !== "undefined" && _.isArray(window[CONSOLE_KEY])
		? window[CONSOLE_KEY]
		: [];
	return redactDeep(entries.slice(-MAX_CONSOLE_ENTRIES));
}

function getNetworkEntries() {
	const entries = typeof window !== "undefined" && _.isArray(window[NETWORK_KEY])
		? window[NETWORK_KEY]
		: [];
	return redactDeep(entries.slice(-MAX_NETWORK_ENTRIES));
}

function getChildReports() {
	const reports = typeof window !== "undefined" && _.isArray(window[CHILD_REPORTS_KEY])
		? window[CHILD_REPORTS_KEY]
		: [];
	return redactDeep(reports.slice(-10));
}

function getDiagnosticEvents() {
	const events = typeof window !== "undefined" && _.isArray(window[EVENTS_KEY])
		? window[EVENTS_KEY]
		: [];
	return redactDeep(events.slice(-MAX_DIAGNOSTIC_EVENTS));
}

function pushChildReport(report) {
	if (typeof window === "undefined" || !report) {
		return;
	}
	if (!_.isArray(window[CHILD_REPORTS_KEY])) {
		window[CHILD_REPORTS_KEY] = [];
	}
	window[CHILD_REPORTS_KEY].push(redactDeep({
		receivedAt: new Date().toISOString(),
		report
	}));
	if (window[CHILD_REPORTS_KEY].length > 10) {
		window[CHILD_REPORTS_KEY].splice(0, window[CHILD_REPORTS_KEY].length - 10);
	}
}

export function recordDiagnosticEvent(eventType, detail = {}) {
	if (typeof window === "undefined") {
		return null;
	}
	if (!_.isArray(window[EVENTS_KEY])) {
		window[EVENTS_KEY] = [];
	}

	const entry = redactDeep(compactForSend({
		timestamp: new Date().toISOString(),
		eventType: _.toString(eventType || "diagnosticEvent").trim() || "diagnosticEvent",
		detail
	}));
	window[EVENTS_KEY].push(entry);
	if (window[EVENTS_KEY].length > MAX_DIAGNOSTIC_EVENTS) {
		window[EVENTS_KEY].splice(0, window[EVENTS_KEY].length - MAX_DIAGNOSTIC_EVENTS);
	}
	return entry;
}

export function enableTemporaryDiagnosticCapture(reason = "manual", durationMs = 120000, context = {}) {
	if (typeof window === "undefined") {
		return null;
	}

	const safeDurationMs = Math.max(1000, Math.min(Number(durationMs) || 120000, 10 * 60 * 1000));
	const captureUntil = Date.now() + safeDurationMs;
	const currentCaptureUntil = getTemporaryDiagnosticCaptureUntil();
	window[TEMP_CAPTURE_UNTIL_KEY] = Math.max(captureUntil, currentCaptureUntil);
	window[TEMP_CAPTURE_REASON_KEY] = _.toString(reason || "manual");
	return recordDiagnosticEvent("temporaryDiagnosticCaptureEnabled", {
		reason,
		durationMs: safeDurationMs,
		captureUntil: new Date(window[TEMP_CAPTURE_UNTIL_KEY]).toISOString(),
		context
	});
}

export function disableTemporaryDiagnosticCapture(reason = "", context = {}) {
	if (typeof window === "undefined") {
		return null;
	}

	const currentReason = _.toString(window[TEMP_CAPTURE_REASON_KEY] || "");
	const currentCaptureUntil = getTemporaryDiagnosticCaptureUntil();
	if (reason && currentReason && currentReason !== reason) {
		return recordDiagnosticEvent("temporaryDiagnosticCaptureDisableSkipped", {
			reason,
			currentReason,
			currentCaptureUntil: currentCaptureUntil ? new Date(currentCaptureUntil).toISOString() : "",
			context
		});
	}

	delete window[TEMP_CAPTURE_UNTIL_KEY];
	delete window[TEMP_CAPTURE_REASON_KEY];
	return recordDiagnosticEvent("temporaryDiagnosticCaptureDisabled", {
		reason,
		previousReason: currentReason,
		previousCaptureUntil: currentCaptureUntil ? new Date(currentCaptureUntil).toISOString() : "",
		context
	});
}

function pushNetworkEntry(entry) {
	if (typeof window === "undefined") {
		return;
	}
	if (!shouldRecordDiagnosticCapture()) {
		return;
	}

	if (!_.isArray(window[NETWORK_KEY])) {
		window[NETWORK_KEY] = [];
	}

	window[NETWORK_KEY].push(redactDeep(_.assign({
		timestamp: new Date().toISOString()
	}, entry)));
	if (window[NETWORK_KEY].length > MAX_NETWORK_ENTRIES) {
		window[NETWORK_KEY].splice(0, window[NETWORK_KEY].length - MAX_NETWORK_ENTRIES);
	}
}

function getRequestUrl(input) {
	if (_.isString(input)) {
		return input;
	}
	if (input && _.isString(input.url)) {
		return input.url;
	}
	if (input && _.isString(input.href)) {
		return input.href;
	}
	return "";
}

function getRequestMethod(input, init) {
	return _.toString((init && init.method) || (input && input.method) || "GET").toUpperCase();
}

function getPerformanceEntries() {
	if (typeof window === "undefined" || !window.performance || !_.isFunction(window.performance.getEntriesByType)) {
		return [];
	}

	try {
		return window.performance.getEntriesByType("resource")
			.slice(-MAX_RESOURCE_ENTRIES)
			.map(entry => ({
				name: summarizeLocation(entry.name),
				initiatorType: entry.initiatorType,
				startTime: Math.round(entry.startTime),
				duration: Math.round(entry.duration),
				transferSize: entry.transferSize || 0,
				encodedBodySize: entry.encodedBodySize || 0
			}));
	} catch (err) {
		return [{
			error: err && err.message ? err.message : `${err}`
		}];
	}
}

function getStateSnapshot() {
	try {
		const user = state.get(["user"]) || {};
		const upn = _.toString(user["custom:upn"] || "").trim();
		const sessions = state.get(["player", "sessions"]) || state.get(["sessions"]) || {};
		return redactDeep({
			appState: {
				initialized: state.get(["appState", "initialized"]),
				needsSessionRestore: state.get(["appState", "needsSessionRestore"]),
				loadingMessage: state.get(["appState", "loadingMessage"]),
				nativeHcaSsoRedirectPending: state.get(["appState", "nativeHcaSsoRedirectPending"]),
				redirectAfterMagicLink: !!state.get(["appState", "redirectAfterMagicLink"]),
				magicLinkProcessing: state.get(["appState", "magicLinkProcessing"]),
				navPanelSelected: state.get(["appState", "navPanel", "selected"])
			},
			user: {
				loggedIn: !!user.loggedIn,
				username: user.username || "",
				identityId: user.identityId || "",
				hcaPrincipal: upn ? upn.split("@")[0] : ""
			},
			selectedOrg: {
				id: state.get(["selectedOrg", "id"]),
				memberId: state.get(["selectedOrg", "member", "id"]),
				userSubId: state.get(["selectedOrg", "member", "userSubId"])
			},
			player: {
				selectedActivityId: state.get(["player", "selectedActivityId"]),
				selectedActivityInstanceId: state.get(["player", "selectedActivityInstanceId"]),
				activitiesLoaded: state.get(["player", "activitiesLoaded"]),
				showSpinner: state.get(["player", "showSpinner"]),
				sessionKeys: _.isPlainObject(sessions) ? Object.keys(sessions).length : (_.isArray(sessions) ? sessions.length : 0)
			}
		});
	} catch (err) {
		return {
			error: err && err.message ? err.message : `${err}`
		};
	}
}

function buildDiagnosticReport(reason = "manual") {
	const version = state.get(["version"]) || {};
	const timeZone = typeof Intl !== "undefined" && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : "";
	const report = {
		reportVersion: "2026-06-15-player-launch-diagnostics-v1",
		reason,
		capturedAt: new Date().toISOString(),
		location: typeof window !== "undefined" && window.location ? summarizeLocation(window.location.href) : null,
		currentPath: getCurrentPath(),
		userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
		language: typeof navigator !== "undefined" ? navigator.language : "",
		onLine: typeof navigator !== "undefined" ? navigator.onLine : null,
		visibilityState: typeof document !== "undefined" ? document.visibilityState : "",
		timeZone,
		screen: typeof window !== "undefined" && window.screen ? {
			width: window.screen.width,
			height: window.screen.height,
			availWidth: window.screen.availWidth,
			availHeight: window.screen.availHeight,
			devicePixelRatio: window.devicePixelRatio
		} : {},
		version: {
			version: version.version || "",
			build: version.build || "",
			buildDate: version.buildDate || ""
		},
		state: getStateSnapshot(),
		storage: {
			localStorage: readStorage(window.localStorage),
			sessionStorage: readStorage(window.sessionStorage)
		},
		diagnosticCapture: getDiagnosticCaptureState(),
		diagnosticEvents: getDiagnosticEvents(),
		webLoginSequenceEvents: getLoggerEvents(),
		consoleEntries: getConsoleEntries(),
		networkRequests: getNetworkEntries(),
		performanceResources: getPerformanceEntries(),
		childReports: getChildReports()
	};

	return redactDeep(report);
}

function createElement(tag, attrs = {}, children = []) {
	const el = document.createElement(tag);
	Object.keys(attrs).forEach(key => {
		if (attrs[key] === null || attrs[key] === undefined) {
			return;
		}
		if (key === "style") {
			Object.assign(el.style, attrs[key]);
		} else if (key === "text") {
			el.textContent = attrs[key];
		} else if (key === "value") {
			el.value = attrs[key];
		} else if (key === "checked") {
			el.checked = attrs[key] === true;
		} else if (key === "selected") {
			el.selected = attrs[key] === true || attrs[key] === "selected";
		} else if (key.indexOf("on") === 0 && _.isFunction(attrs[key])) {
			el.addEventListener(key.substring(2).toLowerCase(), attrs[key]);
		} else {
			el.setAttribute(key, attrs[key]);
		}
	});
	children.forEach(child => el.appendChild(child));
	return el;
}

function copyText(text) {
	if (navigator.clipboard && _.isFunction(navigator.clipboard.writeText)) {
		return navigator.clipboard.writeText(text);
	}

	const textarea = document.createElement("textarea");
	textarea.value = text;
	textarea.setAttribute("readonly", "readonly");
	textarea.style.position = "fixed";
	textarea.style.left = "-9999px";
	document.body.appendChild(textarea);
	textarea.select();
	document.execCommand("copy");
	document.body.removeChild(textarea);
	return Promise.resolve();
}

function downloadText(filename, text) {
	const blob = new Blob([text], { type: "application/json" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function getDiagnosticEndpoint() {
	const endpoint = _.get(env, ["apiGateway", "endpoints", "services", "ambifi", "event"], "/services/ambifi/event");
	const baseUrl = _.toString(_.get(env, ["apiGateway", "baseUrl"], "")).replace(/\/$/, "");
	return baseUrl && endpoint ? `${baseUrl}${endpoint}` : "";
}

function normalizeUserIssue(issue = {}) {
	const categoryIds = USER_ISSUE_CATEGORIES.map(([value]) => value);
	const severityIds = USER_ISSUE_SEVERITIES.map(([value]) => value);
	const category = categoryIds.indexOf(issue.category) !== -1 ? issue.category : "general";
	const severity = severityIds.indexOf(issue.severity) !== -1 ? issue.severity : "annoying";
	return redactDeep({
		category,
		severity,
		description: _.toString(issue.description || "").trim().slice(0, 4000),
		includeDiagnostics: issue.includeDiagnostics !== false,
		reportedAt: new Date().toISOString(),
		source: _.toString(issue.source || "ambifi-cloud").trim() || "ambifi-cloud"
	});
}

function requestChildDiagnosticReports(reason = "userIssueReport", userIssue = {}) {
	if (typeof window === "undefined" || typeof document === "undefined") {
		return;
	}

	try {
		const iframes = Array.prototype.slice.call(document.getElementsByTagName("iframe") || []);
		iframes.forEach((iframe) => {
			if (iframe && iframe.contentWindow && _.isFunction(iframe.contentWindow.postMessage)) {
				iframe.contentWindow.postMessage({
					method: "ambifiRequestChildDiagnosticsReport",
					reason,
					openParent: false,
					parentRequested: true,
					userIssue: normalizeUserIssue(userIssue)
				}, "*");
			}
		});
	} catch (err) {
		console.warn("Unable to request child diagnostic reports", err);
	}
}

function delay(ms) {
	return new Promise(resolve => setTimeout(resolve, ms));
}

function sendReport(report, options = {}) {
	const endpoint = getDiagnosticEndpoint();
	if (!endpoint) {
		return Promise.reject(new Error("Diagnostic endpoint is not configured."));
	}
	const sendReportPayload = buildSendReport(report);
	const playerState = report.state && report.state.player ? report.state.player : {};
	const selectedOrgState = report.state && report.state.selectedOrg ? report.state.selectedOrg : {};
	const selectedOrgId = selectedOrgState.id || "";
	const selectedActivityId = playerState.selectedActivityId || "";
	const selectedActivityInstanceId = playerState.selectedActivityInstanceId || "";
	const userIssue = options.userIssue ? normalizeUserIssue(options.userIssue) : null;
	const optionMeta = _.isPlainObject(options.meta) ? redactDeep(options.meta) : {};

	const req = request.post(endpoint)
		.set("Content-Type", "application/json")
		.timeout({
			response: 15000,
			deadline: 25000
		});
	const jwt = getJwtToken();
	if (jwt) {
		req.set("Authorization", jwt);
	}

	return req.send(JSON.stringify({
		type: "diagnostic",
		diagnosticType: options.diagnosticType || "manualDiagnosticReport",
		source: options.source || "ambifi-cloud-hidden-panel",
		timestamp: new Date().toISOString(),
		meta: _.assign({
			orgId: selectedOrgId,
			selectedOrgId,
			memberId: selectedOrgState.memberId || "",
			userSubId: selectedOrgState.userSubId || "",
			activityId: selectedActivityId,
			instanceId: selectedActivityInstanceId,
			selectedActivityId,
			selectedActivityInstanceId,
			userIssueCategory: userIssue ? userIssue.category : "",
			userIssueSeverity: userIssue ? userIssue.severity : "",
			userIssueDescription: userIssue ? userIssue.description : "",
			userIssueSource: userIssue ? userIssue.source : "",
			userIssueReportedAt: userIssue ? userIssue.reportedAt : "",
			userIssueIncludeDiagnostics: userIssue ? userIssue.includeDiagnostics : undefined,
			hasUserIssueDescription: !!(userIssue && userIssue.description)
		}, optionMeta),
		userIssue,
		report: sendReportPayload
	})).then((res) => {
		if (res && res.body && res.body.success === false) {
			throw new Error(res.body.message || "Diagnostic report was not stored.");
		}
		return res;
	});
}

function sendUserIssueReport(userIssue = {}) {
	const normalizedIssue = normalizeUserIssue(userIssue);
	requestChildDiagnosticReports("userIssueReport", normalizedIssue);
	return delay(USER_ISSUE_CHILD_REPORT_WAIT_MS).then(() => {
		const report = normalizedIssue.includeDiagnostics
			? buildDiagnosticReport("userIssueReport")
			: {
				reportVersion: "2026-06-20-user-issue-no-diagnostics-v1",
				reason: "userIssueReport",
				capturedAt: new Date().toISOString(),
				currentPath: getCurrentPath(),
				state: getStateSnapshot()
			};
		report.userIssue = normalizedIssue;
		return sendReport(report, {
			diagnosticType: "userIssueReport",
			source: "ambifi-cloud-user-issue-report",
			userIssue: normalizedIssue
		});
	});
}

function sendChildInitiatedUserIssueReport(userIssue = {}) {
	const normalizedIssue = normalizeUserIssue(userIssue);
	const report = normalizedIssue.includeDiagnostics
		? buildDiagnosticReport("userIssueReport")
		: {
			reportVersion: "2026-06-20-user-issue-no-diagnostics-v1",
			reason: "userIssueReport",
			capturedAt: new Date().toISOString(),
			currentPath: getCurrentPath(),
			state: getStateSnapshot()
		};
	report.userIssue = normalizedIssue;
	return sendReport(report, {
		diagnosticType: "userIssueReport",
		source: "ambifi-cloud-child-user-issue-report",
		userIssue: normalizedIssue
	});
}

function createOption(value, label, selectedValue) {
	return createElement("option", {
		value,
		text: label,
		selected: value === selectedValue ? "selected" : null
	});
}

function createIssueSelect(options, selectedValue) {
	return createElement("select", {
		value: selectedValue,
		style: {
			width: "100%",
			boxSizing: "border-box",
			background: "#071525",
			color: "#ffffff",
			border: "1px solid #3f5f82",
			borderRadius: "5px",
			padding: "8px",
			fontSize: "14px"
		}
	}, options.map(([value, label]) => createOption(value, label, selectedValue)));
}

function openUserIssueReportDialog(options = {}) {
	if (typeof document === "undefined") {
		return null;
	}

	const existing = document.getElementById("ambifi-user-issue-report-dialog");
	if (existing) {
		existing.remove();
	}

	const status = createElement("div", {
		style: {
			minHeight: "20px",
			fontSize: "12px",
			color: "#d7e6ff",
			marginTop: "8px"
		}
	});
	const categorySelect = createIssueSelect(USER_ISSUE_CATEGORIES, "general");
	const severitySelect = createIssueSelect(USER_ISSUE_SEVERITIES, "annoying");
	const descriptionInput = createElement("textarea", {
		placeholder: "Describe what happened, what you expected, and what you were doing when it occurred.",
		style: {
			width: "100%",
			height: "132px",
			boxSizing: "border-box",
			background: "#071525",
			color: "#ffffff",
			border: "1px solid #3f5f82",
			borderRadius: "5px",
			padding: "10px",
			fontFamily: "Arial, sans-serif",
			fontSize: "14px",
			lineHeight: "1.45",
			resize: "vertical"
		}
	});
	const includeDiagnosticsInput = createElement("input", {
		type: "checkbox",
		checked: true,
		style: {
			marginRight: "8px"
		}
	});
	const setStatus = (message) => {
		status.textContent = message;
	};
	const closeDialog = () => {
		const panel = document.getElementById("ambifi-user-issue-report-dialog");
		if (panel) {
			panel.remove();
		}
	};
	const buttonStyle = {
		border: "1px solid #6f9ed4",
		background: "#174b82",
		color: "#ffffff",
		borderRadius: "5px",
		padding: "9px 13px",
		fontWeight: "600",
		cursor: "pointer"
	};
	const cancelButton = createElement("button", {
		text: "Cancel",
		style: Object.assign({}, buttonStyle, { background: "#243447" }),
		onclick: closeDialog
	});
	const sendButton = createElement("button", {
		text: "Send Report",
		style: Object.assign({}, buttonStyle, { background: "#1f6f42" }),
		onclick: () => {
			const description = _.toString(descriptionInput.value || "").trim();
			if (!description) {
				setStatus("Please describe what happened before sending the report.");
				descriptionInput.focus();
				return;
			}
			sendButton.disabled = true;
			sendButton.style.opacity = "0.65";
			setStatus("Sending report...");
			sendUserIssueReport({
				category: categorySelect.value,
				severity: severitySelect.value,
				description,
				includeDiagnostics: includeDiagnosticsInput.checked,
				source: options.source || "ambifi-cloud"
			}).then((res) => {
				const storedKey = _.get(res, ["body", "storedKey"], "");
				setStatus(storedKey ? `Report sent. Reference: ${storedKey}` : "Report sent. Thank you.");
				setTimeout(closeDialog, 1200);
			}).catch((err) => {
				sendButton.disabled = false;
				sendButton.style.opacity = "1";
				setStatus(`Unable to send report. ${err && err.message ? err.message : err}`);
			});
		}
	});

	const overlay = createElement("div", {
		id: "ambifi-user-issue-report-dialog",
		style: {
			position: "fixed",
			inset: "0",
			zIndex: "2147483647",
			background: "rgba(0, 0, 0, 0.58)",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			padding: "18px",
			boxSizing: "border-box"
		}
	}, [
		createElement("div", {
			style: {
				width: "min(560px, 94vw)",
				background: "#102033",
				color: "#ffffff",
				border: "1px solid #5a7da4",
				borderRadius: "8px",
				boxShadow: "0 24px 80px rgba(0, 0, 0, 0.45)",
				padding: "18px",
				boxSizing: "border-box",
				fontFamily: "Arial, sans-serif"
			}
		}, [
			createElement("div", {
				text: "Report an Issue",
				style: {
					fontSize: "20px",
					fontWeight: "700",
					marginBottom: "6px"
				}
			}),
			createElement("div", {
				text: "Tell us what happened. AmbiFi can include technical details to help troubleshoot.",
				style: {
					fontSize: "13px",
					color: "#d7e6ff",
					marginBottom: "14px"
				}
			}),
			createElement("label", {
				text: "Category",
				style: {
					display: "block",
					fontSize: "12px",
					fontWeight: "700",
					marginBottom: "5px"
				}
			}),
			categorySelect,
			createElement("label", {
				text: "Severity",
				style: {
					display: "block",
					fontSize: "12px",
					fontWeight: "700",
					marginTop: "12px",
					marginBottom: "5px"
				}
			}),
			severitySelect,
			createElement("label", {
				text: "What happened?",
				style: {
					display: "block",
					fontSize: "12px",
					fontWeight: "700",
					marginTop: "12px",
					marginBottom: "5px"
				}
			}),
			descriptionInput,
			createElement("label", {
				style: {
					display: "flex",
					alignItems: "center",
					marginTop: "12px",
					fontSize: "13px",
					color: "#d7e6ff"
				}
			}, [
				includeDiagnosticsInput,
				createElement("span", {
					text: "Include technical details"
				})
			]),
			status,
			createElement("div", {
				style: {
					display: "flex",
					justifyContent: "flex-end",
					gap: "8px",
					marginTop: "14px"
				}
			}, [cancelButton, sendButton])
		])
	]);

	document.body.appendChild(overlay);
	descriptionInput.focus();
	logWebLoginSequence("userIssueReport dialog opened", {
		source: options.source || "ambifi-cloud",
		currentPath: getCurrentPath()
	});
	return overlay;
}

export function sendDiagnosticReport(reason = "manual", options = {}) {
	if (options && options.context) {
		recordDiagnosticEvent(`${reason}:context`, options.context);
	}

	const report = buildDiagnosticReport(reason);
	if (options && options.context) {
		report.context = redactDeep(options.context);
	}

	return sendReport(report, {
		diagnosticType: options.diagnosticType || "manualDiagnosticReport",
		source: options.source || "ambifi-cloud-diagnostics-api",
		userIssue: options.userIssue,
		meta: options.meta
	});
}

function openDiagnosticsPanel(reason = "manual", options = {}) {
	if (typeof document === "undefined") {
		return null;
	}

	const existing = document.getElementById("ambifi-web-diagnostics-panel");
	if (existing) {
		existing.remove();
	}

	if (options && options.context) {
		recordDiagnosticEvent(`${reason}:context`, options.context);
	}

	const report = buildDiagnosticReport(reason);
	if (options && options.context) {
		report.context = redactDeep(options.context);
	}
	const reportText = JSON.stringify(report, null, 2);
	const status = createElement("div", {
		style: {
			minHeight: "20px",
			fontSize: "12px",
			color: "#d7e6ff"
		}
	});
	const textarea = createElement("textarea", {
		value: reportText,
		readonly: "readonly",
		style: {
			width: "100%",
			height: "52vh",
			boxSizing: "border-box",
			background: "#071525",
			color: "#e9f3ff",
			border: "1px solid #3f5f82",
			borderRadius: "6px",
			padding: "10px",
			fontFamily: "Menlo, Consolas, monospace",
			fontSize: "11px",
			lineHeight: "1.45",
			resize: "vertical"
		}
	});
	textarea.value = reportText;

	const setStatus = (message) => {
		status.textContent = message;
	};

	const buttonStyle = {
		border: "1px solid #6f9ed4",
		background: "#174b82",
		color: "#ffffff",
		borderRadius: "5px",
		padding: "8px 12px",
		fontWeight: "600",
		cursor: "pointer"
	};

	const closeButton = createElement("button", {
		text: "Close",
		style: Object.assign({}, buttonStyle, { background: "#243447" }),
		onclick: () => {
			const panel = document.getElementById("ambifi-web-diagnostics-panel");
			if (panel) {
				panel.remove();
			}
		}
	});

	const copyButton = createElement("button", {
		text: "Copy Report",
		style: buttonStyle,
		onclick: () => {
			copyText(textarea.value)
				.then(() => setStatus("Diagnostic report copied. Send it to AmbiFi support."))
				.catch(err => setStatus(`Copy failed: ${err && err.message ? err.message : err}`));
		}
	});

	const downloadButton = createElement("button", {
		text: "Download JSON",
		style: buttonStyle,
		onclick: () => {
			downloadText(`ambifi-diagnostics-${Date.now()}.json`, textarea.value);
			setStatus("Diagnostic JSON downloaded.");
		}
	});

	const sendButton = createElement("button", {
		text: "Send to AmbiFi",
		style: Object.assign({}, buttonStyle, { background: "#1f6f42" }),
		onclick: () => {
			setStatus("Sending diagnostic report...");
			sendReport(report, {
				diagnosticType: options.diagnosticType || "manualDiagnosticReport",
				source: options.source || "ambifi-cloud-hidden-panel",
				userIssue: options.userIssue,
				meta: options.meta
			})
				.then((res) => {
					const storedKey = _.get(res, ["body", "storedKey"], "");
					setStatus(storedKey ? `Diagnostic report sent to AmbiFi: ${storedKey}` : "Diagnostic report sent to AmbiFi.");
				})
				.catch(err => setStatus(`Send failed. Please copy or download instead. ${err && err.message ? err.message : err}`));
		}
	});

	const overlay = createElement("div", {
		id: "ambifi-web-diagnostics-panel",
		style: {
			position: "fixed",
			inset: "0",
			zIndex: "2147483647",
			background: "rgba(0, 0, 0, 0.72)",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			padding: "18px",
			boxSizing: "border-box"
		}
	}, [
		createElement("div", {
			style: {
				width: "min(980px, 96vw)",
				maxHeight: "94vh",
				overflow: "auto",
				background: "#102033",
				color: "#ffffff",
				border: "1px solid #5a7da4",
				borderRadius: "8px",
				boxShadow: "0 24px 80px rgba(0, 0, 0, 0.45)",
				padding: "18px",
				boxSizing: "border-box",
				fontFamily: "Arial, sans-serif"
			}
		}, [
			createElement("div", {
				style: {
					display: "flex",
					justifyContent: "space-between",
					gap: "16px",
					alignItems: "flex-start",
					marginBottom: "12px"
				}
			}, [
				createElement("div", {}, [
					createElement("div", {
						text: "AmbiFi Diagnostics",
						style: {
							fontSize: "20px",
							fontWeight: "700",
							marginBottom: "4px"
						}
					}),
					createElement("div", {
						text: "Use this only when AmbiFi support asks for it. It contains a sanitized browser/session report.",
						style: {
							fontSize: "13px",
							color: "#d7e6ff"
						}
					})
				]),
				closeButton
			]),
			createElement("div", {
				style: {
					display: "flex",
					flexWrap: "wrap",
					gap: "8px",
					marginBottom: "10px"
				}
			}, [copyButton, downloadButton, sendButton]),
			status,
			textarea
		])
	]);

	document.body.appendChild(overlay);
	logWebLoginSequence("webDiagnosticsPanel opened", {
		reason,
		currentPath: getCurrentPath()
	});
	return report;
}

function installConsoleCapture() {
	if (typeof window === "undefined" || (window[GLOBAL_KEY] && window[GLOBAL_KEY].consoleCaptureInstalled)) {
		return;
	}

	if (!_.isArray(window[CONSOLE_KEY])) {
		window[CONSOLE_KEY] = [];
	}

	["log", "info", "warn", "error"].forEach(method => {
		const original = console[method];
		if (!_.isFunction(original) || original.__ambifiDiagnosticWrapped) {
			return;
		}

		const wrapped = function wrappedConsoleMethod(...args) {
			try {
				if (shouldRecordDiagnosticCapture()) {
					window[CONSOLE_KEY].push({
						level: method,
						timestamp: new Date().toISOString(),
						message: redactDeep(args.map(arg => {
							if (_.isString(arg)) {
								return arg;
							}
							if (arg instanceof Error) {
								return {
									name: arg.name,
									message: arg.message,
									stack: arg.stack
								};
							}
							return arg;
						}))
					});
					if (window[CONSOLE_KEY].length > MAX_CONSOLE_ENTRIES) {
						window[CONSOLE_KEY].splice(0, window[CONSOLE_KEY].length - MAX_CONSOLE_ENTRIES);
					}
				}
			} catch (_err) {
				// Never let diagnostics affect app logging.
			}
			return original.apply(console, args);
		};
		wrapped.__ambifiDiagnosticWrapped = true;
		console[method] = wrapped;
	});
}

function installNetworkCapture() {
	if (typeof window === "undefined" || (window[GLOBAL_KEY] && window[GLOBAL_KEY].networkCaptureInstalled)) {
		return;
	}

	if (!_.isArray(window[NETWORK_KEY])) {
		window[NETWORK_KEY] = [];
	}

	if (_.isFunction(window.fetch) && !window.fetch.__ambifiDiagnosticWrapped) {
		const originalFetch = window.fetch;
		const wrappedFetch = function wrappedDiagnosticFetch(input, init) {
			const captureRequest = shouldRecordDiagnosticCapture();
			const startedAt = captureRequest ? Date.now() : 0;
			const method = captureRequest ? getRequestMethod(input, init) : "";
			const url = captureRequest ? summarizeLocation(getRequestUrl(input)) : "";
			return originalFetch.apply(this, arguments)
				.then((response) => {
					if (captureRequest) {
						pushNetworkEntry({
							type: "fetch",
							method,
							url,
							status: response.status,
							ok: response.ok,
							redirected: response.redirected,
							durationMs: Date.now() - startedAt
						});
					}
					return response;
				})
				.catch((err) => {
					if (captureRequest) {
						pushNetworkEntry({
							type: "fetch",
							method,
							url,
							error: err && err.message ? err.message : `${err}`,
							durationMs: Date.now() - startedAt
						});
					}
					throw err;
				});
		};
		wrappedFetch.__ambifiDiagnosticWrapped = true;
		wrappedFetch.__ambifiDiagnosticOriginal = originalFetch;
		window.fetch = wrappedFetch;
	}

	if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
		const proto = window.XMLHttpRequest.prototype;
		if (_.isFunction(proto.open) && _.isFunction(proto.send) && !proto.open.__ambifiDiagnosticWrapped) {
			const originalOpen = proto.open;
			const originalSend = proto.send;

			proto.open = function wrappedDiagnosticXhrOpen(method, url) {
				this.__ambifiDiagnosticRequest = {
					method: _.toString(method || "GET").toUpperCase(),
					url: summarizeLocation(getRequestUrl(url))
				};
				return originalOpen.apply(this, arguments);
			};
			proto.open.__ambifiDiagnosticWrapped = true;
			proto.open.__ambifiDiagnosticOriginal = originalOpen;

			proto.send = function wrappedDiagnosticXhrSend() {
				const xhr = this;
				const captureRequest = shouldRecordDiagnosticCapture();
				const startedAt = captureRequest ? Date.now() : 0;
				const requestInfo = captureRequest ? (xhr.__ambifiDiagnosticRequest || {}) : {};
				const record = (outcome) => {
					if (captureRequest) {
						pushNetworkEntry({
							type: "xhr",
							method: requestInfo.method || "GET",
							url: summarizeLocation(xhr.responseURL || requestInfo.url || ""),
							status: xhr.status || 0,
							readyState: xhr.readyState,
							outcome,
							durationMs: Date.now() - startedAt
						});
					}
				};
				try {
					xhr.addEventListener("loadend", () => record("loadend"), { once: true });
				} catch (_err) {
					// Ignore instrumentation failures.
				}
				return originalSend.apply(this, arguments);
			};
			proto.send.__ambifiDiagnosticWrapped = true;
			proto.send.__ambifiDiagnosticOriginal = originalSend;
		}
	}
}

export function installWebDiagnosticsPanel() {
	if (typeof window === "undefined" || typeof document === "undefined") {
		return () => {};
	}

	installConsoleCapture();
	installNetworkCapture();

	if (window[GLOBAL_KEY] && window[GLOBAL_KEY].installed) {
		return window[GLOBAL_KEY].uninstall || (() => {});
	}

	const api = {
		installed: true,
		consoleCaptureInstalled: true,
		networkCaptureInstalled: true,
		open: openDiagnosticsPanel,
		openIssueReport: openUserIssueReportDialog,
		sendReport: sendDiagnosticReport,
		buildReport: buildDiagnosticReport,
		recordEvent: recordDiagnosticEvent,
		enableTemporaryCapture: enableTemporaryDiagnosticCapture,
		disableTemporaryCapture: disableTemporaryDiagnosticCapture,
		getConsoleEntries,
		getNetworkEntries,
		getDiagnosticEvents,
		uninstall: null
	};

	const onKeyDown = (event) => {
		const key = _.toString(event.key || "").toLowerCase();
		const code = _.toString(event.code || "").toLowerCase();
		const isDKey = key === "d" || code === "keyd";
		const hasShortcutModifiers = event.altKey && event.shiftKey && (event.ctrlKey || event.metaKey);
		if (hasShortcutModifiers && isDKey) {
			event.preventDefault();
			openDiagnosticsPanel("keyboardShortcut");
		}
	};

	window.addEventListener("keydown", onKeyDown, true);
	window[GLOBAL_KEY] = api;
	window.ambifiDiagnostics = openDiagnosticsPanel;
	window.ambifiReportIssue = openUserIssueReportDialog;
	window.ambifiSendDiagnosticReport = sendDiagnosticReport;
	window.ambifiRecordDiagnosticEvent = recordDiagnosticEvent;
	window.ambifiEnableDiagnosticCapture = enableTemporaryDiagnosticCapture;
	window.ambifiDisableDiagnosticCapture = disableTemporaryDiagnosticCapture;

	const params = new URLSearchParams(window.location.search || "");
	const enabledParam = readBooleanParam(params, "ambifiDiagnostics");
	persistDiagnosticsPreferenceFromUrl();
	if (enabledParam === true) {
		setTimeout(() => openDiagnosticsPanel("queryParam"), 500);
	}

	const onMessage = (event) => {
		let data = event && event.data ? event.data : null;
		try {
			if (_.isString(data)) {
				data = JSON.parse(data);
			}
		} catch (_err) {
			return;
		}
		if (!data || typeof data !== "object") {
			return;
		}
		if (data.method === "ambifiChildDiagnosticsReport" || data.type === "ambifiChildDiagnosticsReport") {
			pushChildReport(data.report);
			if (data.reason === "userIssueReport" && data.userIssue && data.parentRequested !== true) {
				sendChildInitiatedUserIssueReport(data.userIssue).catch((err) => {
					console.warn("Unable to send child-initiated user issue report", err);
				});
			}
			if (data.openParent !== false) {
				openDiagnosticsPanel(data.reason || "childFrameReport");
			}
			if (data.requestId && event.source && _.isFunction(event.source.postMessage)) {
				try {
					event.source.postMessage({
						method: "ambifiChildDiagnosticsAck",
						requestId: data.requestId,
						source: "ambifi-cloud"
					}, "*");
				} catch (_err) {
					// Best-effort acknowledgement only.
				}
			}
			return;
		}
		if (data.method === "ambifiOpenDiagnostics" || data.type === "ambifiOpenDiagnostics") {
			openDiagnosticsPanel(data.reason || "childFrameRequest");
		}
	};
	window.addEventListener("message", onMessage, false);

	api.uninstall = () => {
		window.removeEventListener("keydown", onKeyDown, true);
		window.removeEventListener("message", onMessage, false);
		if (window[GLOBAL_KEY] === api) {
			delete window[GLOBAL_KEY];
		}
		if (window.ambifiDiagnostics === openDiagnosticsPanel) {
			delete window.ambifiDiagnostics;
		}
		if (window.ambifiReportIssue === openUserIssueReportDialog) {
			delete window.ambifiReportIssue;
		}
		if (window.ambifiSendDiagnosticReport === sendDiagnosticReport) {
			delete window.ambifiSendDiagnosticReport;
		}
		if (window.ambifiRecordDiagnosticEvent === recordDiagnosticEvent) {
			delete window.ambifiRecordDiagnosticEvent;
		}
		if (window.ambifiEnableDiagnosticCapture === enableTemporaryDiagnosticCapture) {
			delete window.ambifiEnableDiagnosticCapture;
		}
		if (window.ambifiDisableDiagnosticCapture === disableTemporaryDiagnosticCapture) {
			delete window.ambifiDisableDiagnosticCapture;
		}
	};

	logWebLoginSequence("webDiagnosticsPanel installed", {
		shortcut: "Ctrl+Alt+Shift+D or Command+Option+Shift+D"
	});
	return api.uninstall;
}

export { buildDiagnosticReport, openDiagnosticsPanel, openUserIssueReportDialog };
